/**
 * Pure VAT calculation helpers — no Angular dependency, no partner/cart shape knowledge.
 *
 * This file is intentionally duplicated verbatim in both frontends:
 *   widget/src/app/services/vat-calculation.util.ts
 *   smart-widget/src/app/services/utils/vat-calculation.util.ts
 * Keep both copies byte-identical (same rule as custom-css.util.ts).
 *
 * All amounts are integer CENTS. All rates are integer HUNDREDTHS OF A PERCENT
 * (19.00 % is passed as rateHundredths = 1900). Addon custom-field-option prices are
 * NOT taxable lines — they carry no tax rate and are excluded by the caller before
 * these functions ever see them.
 */

export interface TaxableLine {
  netCents: number;
  rateHundredths: number;
}

export interface VatByRate {
  /** Percentage, e.g. `19` or `8.1` — NOT hundredths. */
  rate: number;
  amountCents: number;
}

export interface VatResult {
  byRate: VatByRate[];
  totalCents: number;
}

/**
 * Computes the VAT for a set of taxable lines, proportionally scaled down by any coupon
 * discount applied to the cart/booking.
 *
 * discountedNetCents   = D  — the net total AFTER the coupon (services + addons)
 * undiscountedNetCents = NA — the net total BEFORE the coupon (services + addons)
 * ratio = NA > 0 ? D / NA : 0        (== 1 exactly when there is no coupon)
 * tax_i = round(netCents_i * rateHundredths_i / 10000 * ratio)   // cents, in this order
 *
 * Math.round only — never toFixed, never truncating division. The backend evaluates the
 * same expression in the same order so both sides agree to the cent.
 */
export function calculateVat(
  lines: TaxableLine[],
  discountedNetCents: number,
  undiscountedNetCents: number
): VatResult {
  const ratio = undiscountedNetCents > 0 ? discountedNetCents / undiscountedNetCents : 0;
  const byRateMap = new Map<number, number>();

  for (const line of lines || []) {
    if (!line || !(line.netCents > 0) || !(line.rateHundredths > 0)) {
      continue;
    }

    const taxCents = Math.round(((line.netCents * line.rateHundredths) / 10000) * ratio);
    byRateMap.set(line.rateHundredths, (byRateMap.get(line.rateHundredths) || 0) + taxCents);
  }

  const byRate: VatByRate[] = Array.from(byRateMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([rateHundredths, amountCents]) => ({ rate: rateHundredths / 100, amountCents }));

  const totalCents = byRate.reduce((sum, entry) => sum + entry.amountCents, 0);

  return { byRate, totalCents };
}

/**
 * Computes the VAT already contained in gross (`inclusive`) prices, scaled down by any coupon
 * discount exactly like `calculateVat`.
 *
 * tax_i = round(netCents_i * ratio * rateHundredths_i / (10000 + rateHundredths_i))   // cents
 *
 * Same expression and order as the backend's contained-VAT path, so both agree to the cent.
 */
export function calculateIncludedVat(
  lines: TaxableLine[],
  discountedNetCents: number,
  undiscountedNetCents: number
): VatResult {
  const ratio = undiscountedNetCents > 0 ? discountedNetCents / undiscountedNetCents : 0;
  const byRateMap = new Map<number, number>();

  for (const line of lines || []) {
    if (!line || !(line.netCents > 0) || !(line.rateHundredths > 0)) {
      continue;
    }

    const taxCents = Math.round(((line.netCents * ratio) * line.rateHundredths) / (10000 + line.rateHundredths));
    byRateMap.set(line.rateHundredths, (byRateMap.get(line.rateHundredths) || 0) + taxCents);
  }

  const byRate: VatByRate[] = Array.from(byRateMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([rateHundredths, amountCents]) => ({ rate: rateHundredths / 100, amountCents }));

  const totalCents = byRate.reduce((sum, entry) => sum + entry.amountCents, 0);

  return { byRate, totalCents };
}

/**
 * Only `plus` means the price is NET and the VAT is added on top (Bexio "Inkl. Steuer", Netto).
 * `exclude` puts no VAT on the document (Bexio "Exkl. Steuer"); `inclusive` (the default) and any
 * unset/unknown value already contain it. Neither adds anything.
 */
export function isVatAddedOnTop(vatType?: string): boolean {
  return vatType === 'plus';
}

/**
 * i18n key suffix for the VAT-type label shown next to a price, so a caller composes e.g.
 * `tax_info.plus`. An unset or unknown value reads as `inclusive`.
 */
export function vatTypeLabelKey(vatType?: string): string {
  return vatType === 'plus' || vatType === 'exclude' ? vatType : 'inclusive';
}
